import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ColumnDef,
  IdentifiableModel,
  PaginationOptions,
  Table,
} from 'cs-kevlar-react'
import { useShards } from 'hooks'
import { SupportUserPermission } from '../../api'
import { CustomerAgencyShardMetadata, ShardMetadata } from 'api'
import { hasOneOfPermissions } from 'utils'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Databases'

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'shard',
  pluralAdjective: 'shards',
}

type ValidMenuKey = 'Details'

export type ShardMetadataEx = ShardMetadata & IdentifiableModel
export type CustomerAgencyShardMetadataEx = CustomerAgencyShardMetadata &
  IdentifiableModel

const rootRoute = '/databases'

const Shards: React.FunctionComponent = () => {
  const { initialize, shards } = useShards()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = React.useState(false)

  usePageViewAudit(PageTitle, true, true)

  const viewShardDetails = (shard: ShardMetadataEx | null) => {
    if (shard === null) {
      navigate(rootRoute)
      return
    }
    navigate(`${rootRoute}/${shard.shardId}`)
  }

  const handleMenuClick = (key: string, row: ShardMetadataEx) => {
    if (key === 'Details') {
      viewShardDetails(row)
      return
    }
  }

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await initialize()
      setIsLoading(false)
    }

    loadData()
  }, [])

  const columns: ColumnDef<ShardMetadataEx, ValidMenuKey>[] = React.useMemo(
    () => [
      {
        label: 'ID',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.shardId,
        dataProperty: 'shardId' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: 'RDS ID',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.rdsInstanceId,
        dataProperty: 'rdsInstanceId' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Customers',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.totalAgencies,
        dataProperty: 'totalAgencies' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Licensed Users',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.totalLicensedUsers,
        dataProperty: 'totalLicensedUsers' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Connected Devices',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.totalConnectedDevices,
        dataProperty: 'totalConnectedDevices' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: 'Connected Interfaces',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: ShardMetadataEx) => row.totalConnectedInterfaces,
        dataProperty: 'totalConnectedInterfaces' as keyof ShardMetadataEx,
        filterable: true,
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Details',
                key: 'Details',
              },
            ],
          },
        ],
      },
    ],
    [],
  )

  const tableData: ShardMetadataEx[] = React.useMemo(
    () =>
      shards.map((x) => ({
        ...x,
        id: x.shardId,
      })) as ShardMetadataEx[],
    [shards],
  )

  return (
    <React.Fragment>
      <Table
        ariaLabel="Databases"
        uniqueIdentifier="ShardMetadata"
        paginationOptions={PAGINATION_OPTIONS}
        useDefaultRow
        isLoading={isLoading}
        columns={columns as ColumnDef<ShardMetadataEx, ValidMenuKey>[]}
        handleMenuClick={handleMenuClick}
        data={tableData}
      />
    </React.Fragment>
  )
}

export const ShardsPage: PageDefinition = {
  routes: () => [rootRoute],
  type: [PageType.Support],
  sortOrder: () => 6,
  component: <Shards />,
  navLinkText: PageTitle,
  pageTitle: PageTitle,
  canAccess: (user) => {
    return hasOneOfPermissions(user, [SupportUserPermission.ViewShards])
  },
  buildNavLink: (user) => {
    return rootRoute
  },
}
